import { Component } from '@opten/compiler/metadata/component';
import { queryStringAsObject } from '@opten/url/query-string-as-object';
import { hasClass } from '@opten/dom/has-class';
import { addClass } from '@opten/dom/add-class';
import { toggleClass } from '@opten/dom/toggle-class';
import { removeClass } from '@opten/dom/remove-class';

import {eventNamespaceFactory} from '@opten/events/event-namespace-factory';

declare const mixitup;

export interface MixitupOptions {
	itemSelector?: string;
	filterSelector?: string;
	filterCloseSelector?: string;
	filterAllClass: string;
	filterResetSelector?: string;
	filterGroupTitleSelector?: string;
	enableFilterInUrl?: boolean;
	enableContentReplace?: boolean;

}

interface FilterInformation{
	Type: string;
	Filter: string;
	Html?: string;
	MetaTags?: string;
}

interface FilterContent{
	Id?: number;
	Html?: string;
	Filters?: [];
}

@Component({ className: 'js-mixitup' })
export class MixitupComponent {

	mixer: any;
	filters: HTMLElement[];
	filtersWithFilterMatch: HTMLElement[];
	filtersReset: HTMLElement[];
	filtersClose: HTMLElement[];
	filtersGroupTitle: HTMLElement[];
	filterGroups: string[];
	filterGroupsWithFilterInformationsTop: string[];
	filterGroupsWithFilterInformationsBottom: string[];
	filterAllContent: FilterContent[];
	filterAllContentElement: HTMLElement;
	virtualMixer: any;

	defaultToggle: string = 'all';
	defaultToggleGroup: string = '';
	ignoreDefaultToggleForDefaultToggleGroupIfGroupActive: string = '';
	hasActiveFiltersWithCloseFilter = false;
	isInitialLoad = true;
	hideClass = 'd-none';
	activeClass = 'is-active';

	filterInformationTop: HTMLElement;
	filterInformationTopCurrent: HTMLElement;
	filterInformationTopAll: HTMLElement;
	filterInformationsTop: FilterInformation[];
	filterInformationBottom: HTMLElement;
	filterInformationBottomCurrent: HTMLElement;
	filterInformationBottomAll: HTMLElement;
	filterInformationsBottom: FilterInformation[];
	filterInformationsAllMetaTags: string;

    language: string;


	protected options: MixitupOptions = {
		itemSelector: '.js-mixitup-item',
		filterSelector: '.js-mixitup-filter',
		filterCloseSelector: '.js-mixitup-filter-close',
		filterAllClass: 'js-mixitup-filter-all',
		filterResetSelector: '.js-mixitup-filter-reset',
		filterGroupTitleSelector: '.js-mixitup-filter-group-title',
		enableFilterInUrl: false,
		enableContentReplace: false
	};

	private _eventNamespaceFn = eventNamespaceFactory('merbag.mixitup');

	constructor(public element: HTMLElement, options?: MixitupOptions) {
		this.options = {
			...this.options,
			...options,
		};
		this.options.enableFilterInUrl = this.options.enableFilterInUrl || (element.hasAttribute("data-filter-url") && element.getAttribute("data-filter-url").toLowerCase() == 'true');
		this.options.enableContentReplace = this.options.enableContentReplace || element.hasAttribute("data-content-location");
		var dataLanguage = document.body.getAttribute('data-language-long');
		if (dataLanguage && dataLanguage.length > 0) {
			this.language = dataLanguage;
		}
		this.filters = Array.from(element.querySelectorAll(this.options.filterSelector)) as HTMLElement[];
		this.filtersWithFilterMatch = this.filters.filter(o => o.hasAttribute('data-filter-match'));
		this.filtersClose = Array.from(element.querySelectorAll(this.options.filterCloseSelector)) as HTMLElement[];
		this.filtersReset = Array.from(element.querySelectorAll(this.options.filterResetSelector)) as HTMLElement[];
		this.filtersGroupTitle = Array.from(element.querySelectorAll(this.options.filterGroupTitleSelector)) as HTMLElement[];

		const allFilters = this.filters.filter(o => hasClass(o, this.options.filterAllClass));
		if (allFilters && allFilters.length > 0) {
			this.defaultToggle = allFilters[0].getAttribute('data-filter');
			if (this.defaultToggle !== 'all') {
				this.defaultToggleGroup = allFilters[0].getAttribute('data-group');
				this.ignoreDefaultToggleForDefaultToggleGroupIfGroupActive = allFilters[0].hasAttribute('data-ignore-for-group') ? allFilters[0].getAttribute('data-ignore-for-group') : '';
			}
		}
		if(this.options.enableContentReplace){
			const contentLocation = this.element.getAttribute('data-content-location');
			this.filterAllContentElement = this.element.querySelector('.js-mixitup-content');
			if(contentLocation && contentLocation.length > 0){
				fetch('/umbraco/surface/' + contentLocation, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
						'accept-language': this.language,
						// todo: why? if we change to a api controller we don't have this problem...
						'X-Requested-With': 'XMLHttpRequest' // otherwise accept-language is ignored
					},
					credentials: 'include'
				})
					.then(response => {
						const contentType = response.headers.get('content-type');
						if (contentType && contentType.includes('application/json')) {
							return response.json();
						}
					})
					.then(data => {
						if(data && data.length > 0){
							this.filterAllContent = [];
							data.forEach(element => {
								const filterContentElement = element as FilterContent;
								if(filterContentElement){
									this.filterAllContent.push(filterContentElement);
								}
							});
							let filterAllContentHtml = '';
							this.filterAllContent.forEach(o => 	filterAllContentHtml += o.Html);
							this.mixer = mixitup(element, {
								multifilter: {
									enable: true,
									minSearchLength: 1
								},
								selectors: {
									control: this.options.filterSelector,
								},
								controls: {
									scope: 'local',
									toggleLogic: 'and',
									toggleDefault: this.defaultToggle
								},
								callbacks: {
									onMixEnd: (state) => {
										this.options.enableFilterInUrl ? this.setFilterQuery(state) : null;
										var subsetFilterAllContent = this.getActiveFilterContent();
										this.virtualMixer.dataset(subsetFilterAllContent);	
										this._eventNamespaceFn(element, 'mixend');
									}
								},
								animation: {
									enable: false
								},
								classNames: {
									block: '',
									elementFilter: 'is',
									elementToggle: 'is'
								}
							});
							this.filterAllContentElement.innerHTML = filterAllContentHtml;
							this.virtualMixer = mixitup(this.filterAllContentElement, {
								selectors: {
									target: this.options.itemSelector,
								},
								animation: {
									enable: false
								},
								classNames: {
									block: '',
									elementFilter: 'is',
									elementToggle: 'is'
								},
								data: {
									uidKey: 'Id'
								},
								load: {
								   dataset: this.filterAllContent
								},
								render: {
									target: function (item) {
										return `${item.html}`;
									}
								}
							});
							this.initFiltersAfterMixer();
							this.virtualMixer.configure({
								animation: {
									enable: true
								}
							});
						}
					});
			}

		}
		else{
			this.mixer = mixitup(element, {
				multifilter: {
					enable: true,
					minSearchLength: 1
				},
				selectors: {
					target: this.options.itemSelector,
					control: this.options.filterSelector,
				},
				controls: {
					scope: 'local',
					toggleLogic: 'and',
					toggleDefault: this.defaultToggle
				},
				callbacks: {
					onMixEnd: (state) => {
						this.options.enableFilterInUrl ? this.setFilterQuery(state) : null;
						this._eventNamespaceFn(element, 'mixend');
					}
				},
				animation: {
					enable: false
				},
				classNames: {
					block: '',
					elementFilter: 'is',
					elementToggle: 'is'
				}
			});
			this.initFiltersAfterMixer();
		}
	}

	private getActiveFilterContent(){
		let activeFilterContent: FilterContent[] = [];
		this.filterAllContent.forEach(o => activeFilterContent.push(o));
		const stateFilter = this.getUpdatedGroupsState();
		const groupsState = stateFilter.groupsState;
		let updateRequired = stateFilter.updateRequired;
		if(groupsState && updateRequired == false){
			let ignoreDefaultToggle = false;
			const notEmptyFilterGroups = this.filterGroups.filter(o => groupsState[o] && groupsState[o].length > 0);
			if(notEmptyFilterGroups.length == 1 && notEmptyFilterGroups[0] == this.defaultToggleGroup && groupsState[this.defaultToggleGroup] && groupsState[this.defaultToggleGroup][0] == this.getValueFromSelector(this.defaultToggle)){
				ignoreDefaultToggle = true;
			}
			this.filterGroups.forEach(o => {
				if (groupsState[o] && groupsState[o].length > 0 && 
					((ignoreDefaultToggle || (o !== this.defaultToggleGroup || groupsState[o][0] !== this.getValueFromSelector(this.defaultToggle)))
					 || groupsState[o].length > 1)) {
					const filterGroup = ignoreDefaultToggle ? groupsState[o]:  groupsState[o].filter(f => f !== this.getValueFromSelector(this.defaultToggle));
					activeFilterContent = activeFilterContent.filter(c => c.Filters.filter(f => f[o]).map(x => x[o]).map(x => x.some(r => filterGroup.indexOf(r) >= 0))[0]);
				}
			});
		}
		return activeFilterContent;
	}

	private initFiltersAfterMixer(){
		if(this.filtersReset && this.filtersReset.length > 0){
			this.filtersReset.forEach(o => o.addEventListener('click', () => this.resetFilterGroups()));
		}
		this.initFilterGroups();
		this.initFilterInformationsTop();
		this.initFilterInformationsBottom();
	}

	private initFilterGroups() {
		this.filterGroups = [...new Set(this.filters.map(element => {
			return element.getAttribute('data-group')
		}))];

		this.filterGroupsWithFilterInformationsTop = [...new Set(this.filters.filter(element => {
			return element.hasAttribute('data-group-with-filterinformations') && 
			element.getAttribute('data-group-with-filterinformations').toLowerCase() === 'true' && 
			element.hasAttribute('data-group')
		}).map(element => {
			return element.getAttribute('data-group')
		}))];
		this.filterGroupsWithFilterInformationsBottom = [...new Set(this.filters.filter(element => {
			return element.hasAttribute('data-group-with-filterinformations') && 
			element.getAttribute('data-group-with-filterinformations').toLowerCase() === 'true' && 
			element.hasAttribute('data-group')
		}).map(element => {
			return element.getAttribute('data-group')
		}))];

		const currentQuery = queryStringAsObject(window.location.search);
		let ignoreDefaultGroupLength = false
		if(currentQuery && this.ignoreDefaultToggleForDefaultToggleGroupIfGroupActive && this.ignoreDefaultToggleForDefaultToggleGroupIfGroupActive.length > 0 && currentQuery[this.ignoreDefaultToggleForDefaultToggleGroupIfGroupActive] && currentQuery[this.ignoreDefaultToggleForDefaultToggleGroupIfGroupActive].length > 0){
			ignoreDefaultGroupLength = true;
		}
		this.filterGroups.forEach(o => {
			if (currentQuery && currentQuery[o]) {
				const selector = currentQuery[o].split(',').map(f => '.' + f);
				this.mixer.setFilterGroupSelectors(o, selector);
				this.updateFilterClose(o,selector);
			}
			else if (this.defaultToggleGroup == o) {
				this.mixer.setFilterGroupSelectors(o, ignoreDefaultGroupLength ? [] : this.defaultToggle);
			}
		});
		this.updateResetButtons();

		this.mixer.parseFilterGroups();
		this.updateFiltersGroupTitle();
		this.updateFiltersStates();

		this.mixer.configure({
			animation: {
				enable: true
			}
		});
	}

	private initFilterInformationsTop(){
		this.filterInformationTop = document.querySelector('.js-mixitup-filter-information-top');
		this.filterInformationTopCurrent = document.querySelector('.js-mixitup-filter-information-top-current');
		this.filterInformationTopAll = document.querySelector('.js-mixitup-filter-information-top-all');
		this.filterInformationsTop = [];
		if(this.filterInformationTop){

			const location = this.filterInformationTop.getAttribute('data-location');
			if(location && location.length > 0){
				fetch('/umbraco/surface/' + location, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
						'accept-language': this.language,
						// todo: why? if we change to a api controller we don't have this problem...
						'X-Requested-With': 'XMLHttpRequest' // otherwise accept-language is ignored
					},
					credentials: 'include'
				})
					.then(response => {
						const contentType = response.headers.get('content-type');
						if (contentType && contentType.includes('application/json')) {
							return response.json();
						}
					})
					.then(data => {
						if(data && data.length > 0){
							data.forEach(element => {
								const filterInformationElement = element as FilterInformation;
								if(filterInformationElement){
									this.filterInformationsTop.push(filterInformationElement);
								}
							});
						}
					});
			}
			const metasAllLocation = this.filterInformationTop.getAttribute('data-metas-location');
			if(metasAllLocation && metasAllLocation.length > 0){
				fetch('/umbraco/surface/' + metasAllLocation, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
						'accept-language': this.language,
						// todo: why? if we change to a api controller we don't have this problem...
						'X-Requested-With': 'XMLHttpRequest' // otherwise accept-language is ignored
					},
					credentials: 'include'
				})
					.then(response => {
						const contentType = response.headers.get('content-type');
						if (contentType && contentType.includes('text/html')) {
							return response.text();
						}
					})
					.then(data => {
						if(data && data.length > 0){
							this.filterInformationsAllMetaTags = data;
						}
					});
			}
		}
	}

	private initFilterInformationsBottom(){
		this.filterInformationBottom = document.querySelector('.js-mixitup-filter-information-bottom');
		this.filterInformationBottomCurrent = document.querySelector('.js-mixitup-filter-information-bottom-current');
		this.filterInformationBottomAll = document.querySelector('.js-mixitup-filter-information-bottom-all');
		this.filterInformationsBottom = [];
		if(this.filterInformationBottom){
			var dataLanguage = document.body.getAttribute('data-language-long');
			if (dataLanguage && dataLanguage.length > 0) {
				this.language = dataLanguage;
			}
			const location = this.filterInformationBottom.getAttribute('data-location');
			if(location && location.length > 0){
				fetch('/umbraco/surface/' + location, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
						'accept-language': this.language,
						// todo: why? if we change to a api controller we don't have this problem...
						'X-Requested-With': 'XMLHttpRequest' // otherwise accept-language is ignored
					},
					credentials: 'include'
				})
					.then(response => {
						const contentType = response.headers.get('content-type');
						if (contentType && contentType.includes('application/json')) {
							return response.json();
						}
					})
					.then(data => {
						if(data && data.length > 0){
							data.forEach(element => {
								const filterInformationElement = element as FilterInformation;
								if(filterInformationElement){
									this.filterInformationsBottom.push(filterInformationElement);
								}
							});
						}
					});
			}
		}
	}

	private resetFilterGroups(){
		this.filterGroups = [...new Set(this.filters.map(element => {
			return element.getAttribute('data-group')
		}))];
		this.filterGroups.forEach(o => {
			if (this.defaultToggleGroup == o) {
				this.mixer.setFilterGroupSelectors(o, this.defaultToggle);
			}
			else{
				this.mixer.setFilterGroupSelectors(o, []);
			}
		});
		//this.updateResetButtons();
		this.mixer.parseFilterGroups();
	}

	private setFilterQuery(state: any) {
		if (this.isInitialLoad == false) {
			const stateFilter = this.getUpdatedGroupsState();
			const groupsState = stateFilter.groupsState;
			const newQueryObject = {};
			let updateRequired = stateFilter.updateRequired;
			if (groupsState) {
				let ignoreDefaultGroupLength = stateFilter.ignoreDefaultGroupLength;
				if(updateRequired == false){
					this.hasActiveFiltersWithCloseFilter = false;
					this.filterGroups.forEach(o => {
						if (groupsState[o] && groupsState[o].length > 0 && 
							(o !== this.defaultToggleGroup || groupsState[o][0] !== this.getValueFromSelector(this.defaultToggle) || groupsState[o].length > 1)) {
							newQueryObject[o] = groupsState[o].filter(f => f !== this.getValueFromSelector(this.defaultToggle)).join(',');
						}
						this.updateFilterClose(o,groupsState[o].map(f => '.' + f));
					});
					this.updateResetButtons();
					this.updateFiltersGroupTitle();
					this.updateFiltersStates();
				}
				if(updateRequired){
					this.filterGroups.forEach(o => {
						if (groupsState[o] && groupsState[o].length > 0) {
							const selector = groupsState[o].map(f => '.' + f);
							this.mixer.setFilterGroupSelectors(o, selector);
						}
						else if (this.defaultToggleGroup == o ) {
							this.mixer.setFilterGroupSelectors(o, ignoreDefaultGroupLength ? [] : this.defaultToggle);
						}
					});
				
					this.mixer.parseFilterGroups();
				}
			}
			
			if(updateRequired == false){
				const newUrl = this.buildNewUrl(newQueryObject);
				history.pushState(null, document.title, newUrl);
				this.updateFilterInformations(newQueryObject);
			}
		}
		this.isInitialLoad = false;
	}

	private getUpdatedGroupsState(){
		const groupsState = this.getGroupsState();
		let updateRequired = false;
		let ignoreDefaultGroupLength = false;
		if (groupsState) {

			if(groupsState[this.defaultToggleGroup]){
				if(this.ignoreDefaultToggleForDefaultToggleGroupIfGroupActive && 
					this.ignoreDefaultToggleForDefaultToggleGroupIfGroupActive.length > 0 && 
					groupsState[this.ignoreDefaultToggleForDefaultToggleGroupIfGroupActive] && 
					groupsState[this.ignoreDefaultToggleForDefaultToggleGroupIfGroupActive].length > 0){
					ignoreDefaultGroupLength = true;
				}
				if(((ignoreDefaultGroupLength && groupsState[this.defaultToggleGroup].length > 0) || groupsState[this.defaultToggleGroup].length > 1) && 
					groupsState[this.defaultToggleGroup].indexOf(this.getValueFromSelector(this.defaultToggle)) > -1){
					groupsState[this.defaultToggleGroup] = groupsState[this.defaultToggleGroup].filter(f => f !== this.getValueFromSelector(this.defaultToggle));
					updateRequired = true;
				}
				else if(ignoreDefaultGroupLength == false &&
					groupsState[this.defaultToggleGroup].length < 1){
					updateRequired = true;
				}
			}
		}
		return {groupsState, updateRequired, ignoreDefaultGroupLength};
	}

	private buildNewUrl(newQueryObject: {}) {
		let newUrl = window.location.href.replace(window.location.search, '');
		const notEmptyObjects = Object.keys(newQueryObject).filter(k => newQueryObject[k]);
		if (notEmptyObjects && notEmptyObjects.length > 0) {
			newUrl += '?' + notEmptyObjects
				.map((k) => k + '=' + newQueryObject[k])
				.join('&');
		}
		return newUrl;

	}

	private updateFilterInformations(query: object){
		if(this.filterInformationTopCurrent && this.filterGroupsWithFilterInformationsTop && this.filterGroupsWithFilterInformationsTop.length > 0 && this.filterInformationTopAll && this.filterInformationsTop && this.filterInformationsTop.length > 0){
			let showAllFilterinformationsHtml = true;
			let forceNoIndex = false;
			let filterGroupsWithInformations: FilterInformation[] = [];
			this.filterGroupsWithFilterInformationsTop.forEach(group  => {
				const queryFilter = query[group];
				if(queryFilter){
					filterGroupsWithInformations.push(
						{
							Type : group,
							Filter: queryFilter
						} as FilterInformation
					);
				}
			});
			if(filterGroupsWithInformations.length == 1 && filterGroupsWithInformations[0].Filter.indexOf(',') < 0){
				const currentInformationToShow = this.filterInformationsTop.filter(o => o.Filter == filterGroupsWithInformations[0].Filter).map(o => o.Html);
				if(currentInformationToShow && currentInformationToShow.length == 1){
					this.filterInformationTopCurrent.innerHTML = currentInformationToShow[0];
					showAllFilterinformationsHtml = false;
					this.updateMetaTags(forceNoIndex,this.filterInformationsTop.filter(o => o.Filter == filterGroupsWithInformations[0].Filter).map(o => o.MetaTags)[0]);
				}
			}
			else if(filterGroupsWithInformations.length > 1 || (filterGroupsWithInformations.length == 1 && filterGroupsWithInformations[0].Filter.indexOf(',') > 0)){
				forceNoIndex = true;
			}
			toggleClass(this.filterInformationTopCurrent,this.hideClass, showAllFilterinformationsHtml);
			toggleClass(this.filterInformationTopAll,this.hideClass, !showAllFilterinformationsHtml);
			if(showAllFilterinformationsHtml){
				this.filterInformationTopCurrent.innerHTML = '';
				this.updateMetaTags(forceNoIndex);
			}
		}
		if(this.filterInformationBottomCurrent && this.filterGroupsWithFilterInformationsBottom && this.filterGroupsWithFilterInformationsBottom.length > 0 && this.filterInformationBottomAll && this.filterInformationsBottom && this.filterInformationsBottom.length > 0){
			let showAllFilterinformationsHtml = true;
			let filterGroupsWithInformations: FilterInformation[] = [];
			this.filterGroupsWithFilterInformationsBottom.forEach(group  => {
				const queryFilter = query[group];
				if(queryFilter){
					filterGroupsWithInformations.push(
						{
							Type : group,
							Filter: queryFilter
						} as FilterInformation
					);
				}
			});
			if(filterGroupsWithInformations.length == 1 && filterGroupsWithInformations[0].Filter.indexOf(',') < 0){
				const currentInformationToShow = this.filterInformationsBottom.filter(o => o.Filter == filterGroupsWithInformations[0].Filter).map(o => o.Html);
				if(currentInformationToShow && currentInformationToShow.length == 1){
					this.filterInformationBottomCurrent.innerHTML = currentInformationToShow[0];
					if(currentInformationToShow[0].indexOf('js-form') > 0){
						location.reload();
					}
					showAllFilterinformationsHtml = false;
				}
			}
			toggleClass(this.filterInformationBottomCurrent,this.hideClass, showAllFilterinformationsHtml);
			toggleClass(this.filterInformationBottomAll,this.hideClass, !showAllFilterinformationsHtml);
			if(showAllFilterinformationsHtml){
				this.filterInformationBottomCurrent.innerHTML = '';
			}
		}
	}

	private updateMetaTags(forceNoIndex: boolean, newMetaTags: string = null){		
		const head = document.head;
		newMetaTags = newMetaTags != null && newMetaTags.length > 0 ? newMetaTags : this.filterInformationsAllMetaTags;
		const headMetaTagsToReplace = document.querySelectorAll('.js-meta-tag-async');
		if(headMetaTagsToReplace && headMetaTagsToReplace.length > 0){
			headMetaTagsToReplace.forEach(o => head.removeChild(o));
		}
		if(forceNoIndex){
			newMetaTags += '<meta name="robots" content="noindex, nofollow" class="js-meta-tag-async">';
		}
		let metaTags = new DOMParser().parseFromString(newMetaTags, 'text/html');
		if(metaTags != null && metaTags.body != null && metaTags.body.childNodes.length > 0){			
			metaTags.body.childNodes.forEach(metaTag => {
				if(metaTag != null && metaTag != undefined && metaTag.nodeName.toUpperCase() !== "BR" && metaTag.nodeType != 3){
					head.appendChild(metaTag);
				}
			});
		}
	}

	private updateFilterClose(group: string, filter: string[]) {
		if (this.filtersClose && this.filtersClose.length > 0) {
			this.filtersClose.forEach(filterClose => {
				const filterCloseGroup = filterClose.getAttribute('data-group');
				const filterCloseFilter = filterClose.getAttribute('data-filter');
				if(group.length < 1 && filter.length < 1){
					addClass(filterClose, this.hideClass);
				}
				else if (filterCloseGroup && filterCloseGroup.length > 0 && filterCloseGroup == group){
					if(filterCloseFilter && filterCloseFilter.length > 0 && filter.indexOf(filterCloseFilter) > -1) {
						removeClass(filterClose, this.hideClass);
						this.hasActiveFiltersWithCloseFilter = true;
					}
					else {
						addClass(filterClose, this.hideClass);
					}
				} 
			});
		}
	}
	private updateResetButtons(){
		if(this.filtersReset && this.filtersReset.length > 0){
			this.filtersReset.forEach((o) => toggleClass(o,this.hideClass, this.hasActiveFiltersWithCloseFilter == false));
		}
	}

	private updateFiltersGroupTitle(){
		if(this.filtersGroupTitle && this.filtersGroupTitle.length > 0){
			const groupsState = this.getGroupsState();
			this.filtersGroupTitle.forEach(o => {
				const filterGroup = o.getAttribute('data-group');
				if(filterGroup && groupsState[filterGroup]){
					const showFilterGroupTitle = groupsState[filterGroup].length > 0 && (filterGroup !== this.defaultToggleGroup || groupsState[filterGroup].indexOf(this.getValueFromSelector(this.defaultToggle)) < 0);
					toggleClass(o,this.activeClass,showFilterGroupTitle);
				}
			});
		}
	}

	private updateFiltersStates(){
		if(this.filtersWithFilterMatch && this.filtersWithFilterMatch.length > 0){
			const groupsState = this.getGroupsState();
			const selectedFilters: string[] = [];
			this.filterGroups.forEach(o => {
				if(groupsState[o] && groupsState[o].length > 0){
					const groupSelectedFilters = groupsState[o].filter(f => f !== this.getValueFromSelector(this.defaultToggle)).map(f => o + ":." + f);
					if(groupSelectedFilters.length > 0){
						groupSelectedFilters.forEach(element => {
							selectedFilters.push(element);
						});
					}
				}
			});
			if(selectedFilters.length > 0){
				const selectedGroups =[...new Set(selectedFilters.map(o => o.split(':')[0]))];
				this.filtersWithFilterMatch.forEach(o => {
					const filterMatch = o.getAttribute('data-filter-match');
					const filterGroup = o.getAttribute('data-group');
					if(filterMatch && filterMatch.length > 0){
						let isAvailableFilter = true;
						if(selectedGroups.length > 1 || (filterGroup !== selectedGroups[0])){
							isAvailableFilter = selectedFilters.filter(f => filterMatch.indexOf(f) > -1).length > 0;
						}

						if(isAvailableFilter){
							o.removeAttribute('disabled');
						}
						else{
							o.setAttribute('disabled','disabled');
						}
					}
					
				});
			}
			else{
				this.filtersWithFilterMatch.forEach(o => o.removeAttribute('disabled'));
			}
		}
		
	}

	private getGroupsState() {
		const groupsState = {};
		this.filterGroups.forEach(o => {
			groupsState[o] = this.mixer.getFilterGroupSelectors(o).map(this.getValueFromSelector)
		});
		return groupsState;
	}

	private getValueFromSelector(selector) {
		return selector.replace(/^./, '');
	}
}
